/**
 * ConversationFilters — value object describing a saved or ad-hoc inbox
 * filter for the Conversations module.
 *
 * Stored in `ConversationProfile.filters` as a Prisma Json column. Validated
 * by Zod at the repository boundary via `safeParse` so legacy rows that
 * predate a schema change fall back to the empty filter rather than crashing
 * the inbox.
 *
 * Multi-select on every axis except `chatStatus` (true mutex: a thread is
 * either open or closed). Empty arrays mean "no constraint on this axis".
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";

/**
 * Open/closed mutex. Single-select. `ALL` is the explicit "no
 * constraint" choice — distinct from omitting the filter axis, which
 * would default to `OPEN`.
 *
 * Values exposed as a runtime tuple so frontend pickers can iterate
 * the canonical set without re-declaring (`MODES`-style pattern in
 * `types-system.md`). Reuse the tuple from the Zod schema rather than
 * hardcoding string literals in comparisons or option lists.
 */
export const CONVERSATION_CHAT_STATUS_VALUES = ["OPEN", "CLOSED", "ALL"] as const;
export const ConversationChatStatusSchema = z.enum(CONVERSATION_CHAT_STATUS_VALUES);
export type ConversationChatStatus = z.infer<typeof ConversationChatStatusSchema>;

/**
 * Recency window for "last activity" filtering. Used for SLA-style triage views.
 */
export const ConversationActivityWindowSchema = z.enum(["1h", "24h", "7d", "30d"]);
export type ConversationActivityWindow = z.infer<typeof ConversationActivityWindowSchema>;

/**
 * Special assignee tokens. These resolve server-side:
 *   - "me"          → current user ID
 *   - "unassigned"  → threads with no assignee
 *
 * Any other value is treated as a literal user ID.
 */
export const ASSIGNEE_TOKENS = ["me", "unassigned"] as const;
export type AssigneeToken = (typeof ASSIGNEE_TOKENS)[number];

export const ConversationAssigneeSchema = z.union([z.enum(ASSIGNEE_TOKENS), z.string().min(1)]);
export type ConversationAssignee = z.infer<typeof ConversationAssigneeSchema>;

/**
 * The full filter shape. Every axis is optional with a sensible default so
 * `ConversationFiltersSchema.parse({})` yields a valid "show open threads"
 * filter.
 */
export const ConversationFiltersSchema = z.object({
  /** Multi-select ticket statuses (SUBMITTED, IN_PROGRESS, ...). Empty = no constraint. */
  statuses: z.array(z.nativeEnum(DbEnum.ChatTicketStatus)).default([]),

  /** Single-select open/closed mutex. */
  chatStatus: ConversationChatStatusSchema.default("OPEN"),

  /** Multi-select channel types (deployment.type — WHATSAPP, SLACK, ...). */
  channels: z.array(z.nativeEnum(DbEnum.DeploymentType)).default([]),

  /** Multi-select specific deployment instances. */
  deploymentIds: z.array(z.string().uuid()).default([]),

  /** Multi-select assignees. Mix of special tokens ("me", "unassigned") and user IDs. */
  assignees: z.array(ConversationAssigneeSchema).default([]),

  /** Multi-select response handling modes (AUTO, ON_APPROVAL, MANUAL). */
  handlings: z.array(z.nativeEnum(DbEnum.ChatResponseHandling)).default([]),

  /** Only show threads with at least one attachment. */
  hasAttachment: z.boolean().default(false),

  /** Multi-select agent IDs. */
  agentIds: z.array(z.string().uuid()).default([]),

  /** Multi-select customer IDs (the contact behind the chat). */
  customerIds: z.array(z.string().uuid()).default([]),

  /** Show only threads with activity within this window. */
  lastActivityWithin: ConversationActivityWindowSchema.optional(),

  /** Free-text search on customer name / last message. */
  search: z.string().trim().min(1).max(200).optional(),

  /**
   * Absolute date window on `Chat.lastMessageUpdatedAt`. Both bounds
   * optional so the operator can express "after Mar 1" without a
   * closing edge, or "before Mar 31" without an opening one.
   *
   * ISO 8601 datetime strings on the wire (mirrors `ThreadCursor` and
   * the rest of the API surface). The adapter parses each present
   * bound into a `Date` when assembling the Prisma WHERE clause.
   *
   * AND'd with all other filter axes including `lastActivityWithin`.
   * The two compose: a request can ask for "active in last 24h AND
   * created before Mar 1" if needed; UI may layer a mutex affordance,
   * but the wire contract does not enforce one.
   */
  dateRange: z
    .object({
      from: z.string().datetime().nullable(),
      to: z.string().datetime().nullable()
    })
    .optional()
});

export type ConversationFilters = z.infer<typeof ConversationFiltersSchema>;

/**
 * Empty filter — the canonical fallback when stored data is malformed and
 * the seed for new ad-hoc filtering sessions.
 */
export const EMPTY_CONVERSATION_FILTERS: ConversationFilters = ConversationFiltersSchema.parse({});
